import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../singleton/axios';
import dayjs from 'dayjs';

export interface EarningsTranscriptDate {
  quarter: number;              // Fiscal quarter (for backend queries)
  fiscalYear: number;          // Fiscal year (for backend queries)
  date: string;                // Actual call date (YYYY-MM-DD)
}

export interface EarningsTranscriptDateWithValue extends EarningsTranscriptDate {
  value: string;               // Value for dropdown selection (e.g., "2025-1")
  label: string;               // Display label (e.g., "Q1 2025")
  isCurrentQuarter: boolean;   // Is this the most recent quarter?
}

/**
 * Calculate the actual quarter the transcript represents based on the call date.
 * Companies report earnings for the quarter that just ended, so we subtract 1 quarter.
 */
const getActualQuarterFromCallDate = (callDate: string): { quarter: number; year: number } => {
  const date = dayjs(callDate);
  const callMonth = date.month() + 1; // dayjs months are 0-indexed
  const callYear = date.year();
  
  // Calculate which calendar quarter the call date falls in
  const callQuarter = Math.floor((callMonth - 1) / 3) + 1;
  
  // Subtract 1 quarter to get the actual quarter being reported on
  let actualQuarter = callQuarter - 1;
  let actualYear = callYear;
  
  // Handle year boundary (Q1 - 1 = Q4 of previous year)
  if (actualQuarter === 0) {
    actualQuarter = 4;
    actualYear = callYear - 1;
  }
  
  return { quarter: actualQuarter, year: actualYear };
};

export const useEarningsTranscriptDates = (symbol: string | undefined) => {
  return useQuery({
    queryKey: ['earningsTranscriptDates', symbol],
    queryFn: async () => {
      if (!symbol) {
        throw new Error('Symbol is required');
      }

      const response = await api.get<EarningsTranscriptDate[]>('/earnings-transcript-dates', {
        params: {
          symbol: symbol.toUpperCase(),
        },
      });

      // Transform the response to show actual quarters based on call dates
      const transformedData: EarningsTranscriptDateWithValue[] = response.data.map((item, index) => {
        // Calculate the actual quarter this transcript represents
        const actualQuarter = getActualQuarterFromCallDate(item.date);
        
        return {
          ...item,
          value: `${actualQuarter.year}-${actualQuarter.quarter}`, // Use actual quarter for dropdown value
          label: `Q${actualQuarter.quarter} ${actualQuarter.year}`, // Show actual quarter in label (e.g., "Q1 2025")
          isCurrentQuarter: index === 0, // First item is most recent
        };
      });

      return transformedData;
    },
    enabled: !!symbol,
    staleTime: 2 * 60 * 1000, // 2 minutes - fresh data for new transcript availability
    gcTime: 1000 * 60 * 30, // 30 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};